import {
  type BotMessageTypes,
  type BotTypes,
} from '@/pages/finance/components/shared/TelegramSettings';
import { COMMON_API_BASE_URL, VITE_BM_API_BASE_URL } from '@env';
import { createApi } from '@reduxjs/toolkit/query/react';

import { createBaseQueryWith401Retry } from './rtk-query-api';

export interface CampaignBudgetMonitorOut {
  _id: string;
  enabled: boolean;
  defaultMinBudget: number;
}
export interface TgChatOut {
  _id: string;
  chatUId: number;
  username: string;
}
export interface BotSettingsOut {
  availableMessageTypes: BotMessageTypes[];
}
export interface BotVerificationTgCodeOut {
  code: string;
}

export const commonApi = createApi({
  reducerPath: 'commonApi',
  baseQuery: createBaseQueryWith401Retry(`${COMMON_API_BASE_URL}/`),
  tagTypes: ['CampaignBudgetMonitor', 'TgChat', 'BotSettings'],
  endpoints: (builder) => ({
    // ...
    createCampaignBudgetMonitor: builder.mutation<
      CampaignBudgetMonitorOut,
      { accountId: string; defaultMinBudget: number }
    >({
      query: ({ accountId, defaultMinBudget }) => ({
        url: `${VITE_BM_API_BASE_URL}/bm/campaign_budget_monitor/campaign_budget_monitors`,
        method: 'POST',
        body: { accountId, defaultMinBudget },
      }),
      invalidatesTags: [{ type: 'CampaignBudgetMonitor', id: 'LIST' }],
    }),

    getCampaignBudgetMonitors: builder.query<CampaignBudgetMonitorOut[], string>({
      query: (accountId) => ({
        url: `${VITE_BM_API_BASE_URL}/bm/campaign_budget_monitor/campaign_budget_monitors`,
        method: 'GET',
        params: { accountId },
      }),
      providesTags: (res) =>
        res?.length
          ? [
              ...res.map((m) => ({ type: 'CampaignBudgetMonitor' as const, id: m._id })),
              { type: 'CampaignBudgetMonitor' as const, id: 'LIST' },
            ]
          : [{ type: 'CampaignBudgetMonitor' as const, id: 'LIST' }],
    }),

    setCampaignBudgetMonitorEnabled: builder.mutation<
      CampaignBudgetMonitorOut,
      { monitorId: string; enabled: boolean }
    >({
      query: ({ monitorId, enabled }) => ({
        url: `${VITE_BM_API_BASE_URL}/bm/campaign_budget_monitor/campaign_budget_monitors/${monitorId}/enabled`,
        method: 'PATCH',
        body: { enabled },
      }),
      invalidatesTags: (res, err, { monitorId }) => [
        { type: 'CampaignBudgetMonitor', id: monitorId },
        { type: 'CampaignBudgetMonitor', id: 'LIST' },
      ],
    }),

    setCampaignBudgetMinBudget: builder.mutation<
      CampaignBudgetMonitorOut,
      { monitorId: string; defaultMinBudget: number }
    >({
      query: ({ monitorId, defaultMinBudget }) => ({
        url: `${VITE_BM_API_BASE_URL}/bm/campaign_budget_monitor/campaign_budget_monitors/${monitorId}/budget`,
        method: 'PATCH',
        body: { defaultMinBudget },
      }),
      invalidatesTags: (res, err, { monitorId }) => [
        { type: 'CampaignBudgetMonitor', id: monitorId },
        { type: 'CampaignBudgetMonitor', id: 'LIST' },
      ],
    }),

    // tg_chat остаётся на абсолютных URL — заголовок Authorization всё равно подставится
    getTgChats: builder.query<TgChatOut[], void>({
      query: () => ({
        url: `/tg_chat/tg_chats`,
        method: 'GET',
      }),
      providesTags: (res) =>
        res?.length
          ? [
              ...res.map((c) => ({ type: 'TgChat' as const, id: c._id })),
              { type: 'TgChat' as const, id: 'LIST' },
            ]
          : [{ type: 'TgChat' as const, id: 'LIST' }],
    }),

    deleteTgChat: builder.mutation<void, string>({
      query: (id) => ({
        url: `/tg_chat/tg_chats/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'TgChat', id: 'LIST' }],
    }),

    getTgCode: builder.mutation<BotVerificationTgCodeOut, void>({
      query: () => ({
        url: `/tg_chat/tg_chat_verification/code`,
        method: 'POST',
      }),
    }),

    getBotSettings: builder.query<BotSettingsOut[], string>({
      query: (accountId) => ({
        url: `bot/bot_settings`,
        method: 'GET',
        params: { accountId },
      }),
      providesTags: [{ type: 'BotSettings', id: 'LIST' }],
    }),

    setBotMessageTypes: builder.mutation<
      BotSettingsOut,
      { accountId: string; type: BotTypes; messageTypes: BotMessageTypes[] }
    >({
      query: ({ accountId, type, messageTypes }) => ({
        url: `bot/bot_settings/message_types`,
        method: 'PATCH',
        body: { accountId, type, messageTypes },
      }),
      invalidatesTags: [{ type: 'BotSettings', id: 'LIST' }],
    }),

    createBotSettings: builder.mutation<BotSettingsOut, { accountId: string; type: BotTypes }>({
      query: ({ accountId, type }) => ({
        url: `bot/bot_settings`,
        method: 'POST',
        body: { accountId, type },
      }),
      invalidatesTags: [{ type: 'BotSettings', id: 'LIST' }],
    }),
  }),
});

export const extendedCommonApi = commonApi.injectEndpoints({
  endpoints: (builder) => ({
    deletePermissionByAccountId: builder.mutation<void, string>({
      query: (accountId) => ({
        url: `permission/permissions/${accountId}`,
        method: 'DELETE',
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetCampaignBudgetMonitorsQuery,
  useCreateCampaignBudgetMonitorMutation,
  useSetCampaignBudgetMonitorEnabledMutation,
  useSetCampaignBudgetMinBudgetMutation,
  useGetTgChatsQuery,
  useDeleteTgChatMutation,
  useGetTgCodeMutation,
  useGetBotSettingsQuery,
  useSetBotMessageTypesMutation,
  useCreateBotSettingsMutation,
} = commonApi;

export const { useDeletePermissionByAccountIdMutation } = extendedCommonApi;
